import { displayText } from "../../../locales/display";
import { msg } from "../../../locales/translate";
export interface ReportsExportFilters {
	client_ids?: number[];
	from_date?: string;
	to_date?: string;
	status?: string;
}

export interface CsvReport {
	id: number | string;
	client_id: number | string;
	date: string;
	status?: string | null;
	data?: { name?: string; totals?: Record<string, Record<string, number | string | null | undefined>> } | null;
}

const metrics = () => [
	["Turnover", "total_amount"],
	[msg("adjustmentIn"), "total_ajustment_in"],
	[msg("adjustmentOut"), "total_ajustment_out"],
	["Refunds", "total_refunds"],
	["Chargebacks", "total_chargebacks"],
	["OCT", "total_oct"],
	["Rollings", "total_rollings"],
	[msg("releaseRollings"), "total_release_rollings"],
	["Fees", "total_fees"],
	["Net", "total_net"],
] as const;

const quote = (value: string) => /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
// Text supplied by clients must not become a spreadsheet formula.
const textCell = (value: unknown) => {
	const text = String(value ?? "");
	return quote(/^[\s]*[=+@-]/.test(text) || /^[\t\r\n]/.test(text) ? `'${text}` : text);
};
const amountCell = (value: unknown) => {
	if (value == null || value === "") return "";
	if (typeof value === "number" && Number.isFinite(value)) return String(value);
	if (typeof value === "string" && /^-?\d+(?:\.\d+)?$/.test(value)) return value;
	throw new Error(msg("aReportContainsAnInvalidAmountCsvWasNotDownloaded"));
};

export const buildReportsCsv = (reports: CsvReport[]) => {
	const lines = [[msg("reportId"), msg("clientId_8726db01"), msg("client"), msg("date_99c40ab4"), msg("status"), msg("currency"), ...metrics().map(([label]) => displayText(label))].join(",")];
	for (const report of reports) {
		const entries = Object.entries(report.data?.totals ?? {});
		if (!entries.length) entries.push(["", {}]);
		for (const [currency, totals] of entries) {
			const status = report.status === "payed" ? "Paid" : report.status === "created" ? "Created" : report.status === "pending" ? "Pending" : report.status;
			lines.push([
				...[report.id, report.client_id, report.data?.name, report.date?.slice(0, 10), status ? displayText(status) : status, currency].map(textCell),
				...metrics().map(([, key]) => amountCell(totals?.[key])),
			].join(","));
		}
	}
	return "\uFEFF" + lines.join("\r\n") + "\r\n";
};

// Fetch each page using the server's pagination, and never save a partial export.
export async function loadReportsForCsv(
	loadPage: (page: number) => Promise<unknown>,
	onProgress: (loaded: number, total: number) => void = () => {},
) {
	const reports: CsvReport[] = [];
	const seen = new Set<string>();
	let total: number | undefined;
	let perPage: number | undefined;
	for (let page = 1; ; page++) {
		const response = await loadPage(page) as { rows?: CsvReport[]; total?: number; page?: number; perPage?: number } | null;
		if (!response || !Array.isArray(response.rows) || !Number.isSafeInteger(response.total) || response.total! < 0 ||
			!Number.isSafeInteger(response.perPage) || response.perPage! <= 0 || response.page !== page) {
			throw new Error(msg("invalidReportPaginationCsvWasNotDownloaded"));
		}
		if (total !== undefined && (response.total !== total || response.perPage !== perPage)) {
			throw new Error(msg("theReportListChangedDuringDownloadPleaseTryAgain"));
		}
		total = response.total!;
		perPage = response.perPage!;
		if (response.rows.length !== Math.min(perPage, total - reports.length)) {
			throw new Error(msg("couldNotLoadAllReportsPleaseTryAgain"));
		}
		for (const report of response.rows) {
			if (report?.id == null || seen.has(String(report.id))) throw new Error(msg("theReportListChangedDuringDownloadPleaseTryAgain"));
			seen.add(String(report.id));
			reports.push(report);
		}
		onProgress(reports.length, total);
		if (reports.length === total) return reports;
	}
}
